import { fetchData } from './dataFetching';
import { transformData } from './dataTransformation';
import { getOriginERL } from '../../config/siteConfig';

const holeColumns = {
  'Hole No': 'Hole_ID',
  'X': 'NAT_East',
  'Y': 'NAT_North',
  'Z': 'NAT_RL',
  'Azimuth': 'Collar_Azimuth',
  'Dip': 'Collar_Dip',
  'Depth': 'Max_Depth',
};

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};

class HoleDataCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  async getData(hole_url, assay_url) {
    const cacheKey = `${hole_url}_${assay_url}`;
    
    // Return cached data if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey);
    }

    // Start loading data
    const loadingPromise = this.loadData(hole_url, assay_url);
    this.loadingPromises.set(cacheKey, loadingPromise);

    try {
      const data = await loadingPromise;
      this.cache.set(cacheKey, data);
      this.loadingPromises.delete(cacheKey);
      return data;
    } catch (error) {
      this.loadingPromises.delete(cacheKey);
      throw error;
    }
  }

  async loadData(hole_url, assay_url, attempt = 0) {
    try {
      const data = await fetchData(hole_url, assay_url, holeColumns, assayColumns);
      const transformed = transformData(data, holeColumns, assayColumns, getOriginERL());
      return transformed;
    } catch (error) {
      // Check if it's a retryable error
      const errorMessage = error?.message || error?.toString() || String(error) || '';
      const errorString = errorMessage.toLowerCase();
      const errorName = (error?.name || '').toLowerCase();
      const errorType = (error?.type || '').toLowerCase();

      const isHttp2Error = 
        errorString.includes('http2') ||
        errorString.includes('err_http2_protocol_error') ||
        errorString.includes('protocol_error') ||
        errorName.includes('http2') ||
        errorType.includes('http2');

      const isNetworkError = 
        errorString.includes('failed to fetch') ||
        errorString.includes('networkerror') ||
        errorString.includes('network') ||
        errorString.includes('aborted') ||
        errorString.includes('timeout') ||
        errorString.includes('load failed') ||
        errorString.includes('could not load') ||
        errorString.includes('fetch') ||
        errorName.includes('network') ||
        errorName.includes('abort') ||
        errorType === 'networkerror' ||
        errorType === 'abort';

      const isRetryableError = isHttp2Error || isNetworkError;

      // Always retry network/HTTP2 errors indefinitely
      // For other errors, retry up to 100 times as a safety measure
      if (isRetryableError || attempt < 100) {
        const backoffDelay = Math.min(Math.pow(2, Math.min(attempt, 5)) * 1000, 30000);
        const errorInfo = errorMessage || errorName || 'Unknown error';
        console.warn(`[holeDataCache] Attempt ${attempt + 1} failed for ${hole_url}/${assay_url}${isRetryableError ? ' (network error)' : ''}, retrying in ${backoffDelay/1000}s...`, errorInfo);
        
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return this.loadData(hole_url, assay_url, attempt + 1);
      }

      // Only throw if we've truly exhausted retries (shouldn't happen for network errors)
      throw error;
    }
  }

  // Get filtered data for specific dataset and hole type
  getFilteredData(hole_url, assay_url, dataset, holeType) {
    const cacheKey = `${hole_url}_${assay_url}`;
    const allData = this.cache.get(cacheKey);
    
    if (!allData) {
      return null;
    }

    // Filter the data based on dataset and hole type
    const filteredData = {};
    Object.keys(allData).forEach(holeId => {
      const holeData = allData[holeId];
      if (holeData.data.DataSet === dataset && holeData.data.Hole_Type === holeType) {
        filteredData[holeId] = holeData;
      }
    });

    return filteredData;
  }

  // Clear cache if needed
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }
}

// Export singleton instance
export const holeDataCache = new HoleDataCache();
